import React from 'react'
import { useSeo } from '../../lib/i18n'

export default function LinksEn() {
  useSeo({
    title: 'Useful Links & Forms | Borgs Driving School Kalundborg',
    description: 'Useful links, forms and self-service portals for driving school students at Borgs Driving School in Kalundborg.',
    lang: 'en',
    pathname: '/en/links'
  })

  const usefulLinks = [
    {
      id: 'gondrive',
      label: "Go'n Drive login",
      url: 'https://gondrive.com/portal',
      description: 'Log in to the student portal to track your progress, book lessons, and see schedules.',
      icon: 'fa-solid fa-right-to-bracket',
      isPdf: false
    },
    {
      id: 'ansoegning',
      label: 'Ansøgning om kørekort – udskriv formular',
      url: 'https://cdn.fstyr.dk/faerdselsstyrelsen/Media/639223063102384021/kk001-ansoegning-om-koerekort-p23-2026.pdf',
      description: 'Official application form (P23) from the Danish Road Safety Agency for printing.',
      icon: 'fa-solid fa-file-pdf',
      isPdf: true
    },
    {
      id: 'samtykke',
      label: 'Samtykkeerklæring til kørekortelever under 18 år',
      url: 'https://cdn.fstyr.dk/faerdselsstyrelsen/Media/638161826116299623/Samtykkeerkl%C3%A6ring%20ved%20ans%C3%B8gning%20om%20k%C3%B8rekort%20-%20%20Under%2018%20%C3%A5r%20-%20(P23T).pdf',
      description: 'Parental consent declaration required for student drivers under 18 years old.',
      icon: 'fa-solid fa-file-signature',
      isPdf: true
    },
    {
      id: 'fuldmagt',
      label: 'Giv fuldmagt til bestilling af prøver',
      url: '/_assets/vejleding-ny-dk-og-uk.pdf',
      description: 'Guide to granting the driving school power of attorney to book theory and practical driving tests.',
      icon: 'fa-solid fa-file-pdf',
      isPdf: true
    },
    {
      id: 'borgerservice',
      label: 'Bestil tid i Borgerservice',
      url: 'https://reservation.frontdesksuite.com/kalundborg/borgerservice/Home/Index?pageId=9e705649-6b7f-4b8d-aa42-8a167e370e51&culture=da&uiCulture=da',
      description: 'Book an appointment with Kalundborg Citizen Service (Borgerservice) to submit your papers.',
      icon: 'fa-solid fa-calendar-check',
      isPdf: false
    },
    {
      id: 'laegeerklaering',
      label: 'Digital lægeerklæring',
      url: 'https://www.borger.dk/vaelg-kommune-obligatorisk?actionPageId=065ca8f9-a1f5-4946-ada7-12e163f568df&selfserviceId=3c14cbe7-1aaf-4d05-b71b-0c63b217796b',
      description: 'Create and submit your digital medical declaration via Borger.dk before doctor visits.',
      icon: 'fa-solid fa-user-doctor',
      isPdf: false
    },
    {
      id: 'teoriproever',
      label: 'Prøv en af Færdselsstyrelsens digitale teoriprøver',
      url: 'https://dtp.fstyr.dk/',
      description: 'Practice with official digital demo theory tests from the Danish Road Safety Agency.',
      icon: 'fa-solid fa-laptop-file',
      isPdf: false
    },
    {
      id: 'bilens-teknik',
      label: 'Bilens teknik',
      url: 'https://kajkagen.dk',
      description: 'Practice vehicle inspection questions and get ready for your practical driving test at Kajkagen.dk.',
      icon: 'fa-solid fa-car',
      isPdf: false
    }
  ]

  return (
    <div className="bg-slate-50 min-h-screen py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Page Header */}
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="font-header text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Links &amp; Forms
          </h1>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            Here you will find practical links, official forms, and citizen self-service shortcuts for your driver training course.
          </p>
        </div>

        {/* Links Cards List */}
        <div className="space-y-4">
          {usefulLinks.map((item) => (
            <a
              key={item.id}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white p-6 sm:p-7 border-2 border-slate-200 hover:border-emerald-600 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 block"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 text-xl group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <i className={item.icon} aria-hidden="true"></i>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-header text-lg sm:text-xl font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">
                      {item.label}
                    </h2>
                    {item.isPdf && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 px-2 py-0.5 rounded">
                        PDF
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm font-bold text-emerald-700 shrink-0 self-end sm:self-center">
                <span>Open link</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" aria-hidden="true"></i>
              </div>
            </a>
          ))}
        </div>

        {/* Informative Note */}
        <div className="bg-emerald-900 text-white p-6 sm:p-8 rounded-xl shadow-sm space-y-2">
          <div className="flex items-center gap-3">
            <i className="fa-solid fa-circle-info text-[#8cc73d] text-xl" aria-hidden="true"></i>
            <h3 className="font-header text-lg font-bold text-white">Need help with the paperwork?</h3>
          </div>
          <p className="text-sm text-emerald-100 leading-relaxed">
            We are always here to help you get started with the applications and required documents at Borgs Driving School in Kalundborg.
          </p>
        </div>
      </div>
    </div>
  )
}
